import { faChevronDown, faChevronRight } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { create } from "zustand";

export type PanelId = "project" | "chat" | "parcels" | "files";

const STORAGE_KEY = "workspacePanels";
const DEFAULT_OPEN: Record<PanelId, boolean> = {
  project: true,
  chat: true,
  parcels: false,
  files: false,
};

// Zelfde aanpak als map/viewstate.ts: localStorage in een try, want een volle of
// geblokkeerde storage mag de view niet slopen.
const load = (): Record<PanelId, boolean> => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? { ...DEFAULT_OPEN, ...JSON.parse(raw) } : DEFAULT_OPEN;
  } catch {
    return DEFAULT_OPEN;
  }
};

interface PanelState {
  open: Record<PanelId, boolean>;
  toggle: (id: PanelId) => void;
}

export const usePanels = create<PanelState>((set, get) => ({
  open: load(),
  toggle: (id: PanelId) => {
    const open = { ...get().open, [id]: !get().open[id] };
    set({ open });
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(open));
    } catch {
      // niet kunnen onthouden is geen reden om niets te doen
    }
  },
}));

export const Panel = ({
  id,
  title,
  count,
  grow,
  children,
}: {
  id: PanelId;
  title: string;
  count?: number;
  // Een open vak met `grow` vult de resterende hoogte (de chat); de rest krijgt
  // wat het nodig heeft.
  grow?: boolean;
  children: React.ReactNode;
}) => {
  const isOpen = usePanels((s) => s.open[id]);
  const toggle = usePanels((s) => s.toggle);

  return (
    <div className={`flex flex-col border-b ${isOpen && grow ? "min-h-0 flex-1" : "shrink-0"}`}>
      <button
        onClick={() => toggle(id)}
        className="flex items-center gap-1.5 px-2 py-1.5 text-[11px] uppercase tracking-wide text-gray-500 hover:text-black"
      >
        <FontAwesomeIcon icon={isOpen ? faChevronDown : faChevronRight} className="w-2.5" />
        <span>{title}</span>
        {count !== undefined && <span className="text-gray-400">({count})</span>}
      </button>
      {isOpen && (
        <div className={grow ? "flex min-h-0 flex-1 flex-col" : "max-h-64 overflow-y-auto"}>
          {children}
        </div>
      )}
    </div>
  );
};
